import React, { useState } from 'react';
import { Stack, Typography, Button, List, ListItem, ListItemButton, CircularProgress } from '@mui/material';
import { Add } from '@mui/icons-material';
import { BorderedBlock } from '@shared/ui/common/BorderedBlock';
import PurchaseOrderItemPreview from '@features/purchasing/components/preview/PurchaseOrderItemPreview';
import { PurchaseOrder, PurchaseOrderItemStatus } from '@domain/purchasing';
import { usePurchasing } from '@features/purchasing';
import { useToast } from '@shared/hooks/useToast';
import { Product } from '@domain/product/models/Product';
import { getMainSupplierIdForProduct } from '@domain/product/getMainSupplierIdForProduct';
import { useBottomSheet } from '@shared/shell/bottomsheet/useBottomSheet';
import { usePurchaseOrderItems } from '@features/purchasing/hooks/usePurchaseOrderItems';
import { PurchaseOrderDraft } from '@features/purchasing/components/details/PurchaseOrderDraft';

interface ProductPurchaseOrderListProps {
  product: Product
  onItemClick: (purchaseOrder: PurchaseOrder) => void;
}

const ProductPurchaseOrderList: React.FC<ProductPurchaseOrderListProps> = ({
  product,
  onItemClick,
}) => {

  const { createPurchaseOrder } = usePurchasing();
  const { openBottomSheet } = useBottomSheet();

  const [isCreatingDraft, setIsCreatingDraft] = useState(false);

  const toast = useToast();


  const [showAll, setShowAll] = useState(false);

  const { purchaseOrderItems } = usePurchaseOrderItems({
    filters: {
      productId: product.id,
      availableDaysAgo: 0,
    }
  });

  const handleCreateDraftPurchaseOrder = async () => {
    if (product?.id) {
      setIsCreatingDraft(true);
      try {
        // Create draft purchase order directly via repository/service
        const createdPurchaseOrder = await createPurchaseOrder({
          supplierId: getMainSupplierIdForProduct(product),
          items: [
            {
              product,
              status: PurchaseOrderItemStatus.PLANNED,
            }
          ],
          orderDate: new Date(),
          draft: true,
        });

        if (createdPurchaseOrder?.id) {
          openBottomSheet({
            title: 'Purchase Order Draft',
            component: <PurchaseOrderDraft purchaseOrderId={createdPurchaseOrder.id} />,
            closeOnBackdropClick: false,
          });
          setIsCreatingDraft(false);
        }
      } catch (error) {
        toast.error("Failed to create draft purchase order.");
      } finally {
        setIsCreatingDraft(false);
      }
    }
  };

  return (
    <BorderedBlock
      sx={{ p: 0 }}
      header={
        <Stack direction="row" alignItems="center">
          <Typography flex={1} variant="subtitle2">Purchase Order</Typography>

          <Button disabled={isCreatingDraft} endIcon={
            isCreatingDraft ? <CircularProgress size={20} /> : <Add />} variant="text" color="primary" onClick={handleCreateDraftPurchaseOrder}>
            Create Purchase Order
          </Button>

        </Stack>
      }
    >
      <List>
        {(!purchaseOrderItems || purchaseOrderItems.length === 0) ? (
          <ListItem>
            <Typography variant="subtitle2" color="text.secondary">No purchase order items found</Typography>
          </ListItem>
        ) : null}
        {purchaseOrderItems && purchaseOrderItems.length > 0 && (
          purchaseOrderItems.slice(0, showAll ? undefined : 3).map((item) => (
            <ListItemButton key={item.id} onClick={() => item.purchaseOrder && onItemClick(item.purchaseOrder)}>
              <PurchaseOrderItemPreview purchaseOrderItem={item} />
            </ListItemButton>
          ))
        )}
        {purchaseOrderItems && purchaseOrderItems.length > 3 && (
          <ListItem>
            <Button sx={{ alignSelf: 'flex-start' }} variant="text" color="primary" onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Show Less' : `Show ${purchaseOrderItems.length - 3} more`}
            </Button>
          </ListItem>
        )}
      </List>
    </BorderedBlock>
  );
}

export default ProductPurchaseOrderList;
